/**
 * Bring every file already on file to the automatic names ("C.I._Mario_Rossi_scadenza_
 * 20-05-2031.pdf"), once. New uploads are named on arrival; this is for what came before.
 * It looks first (how many, a few examples) and renames only on a second click. The old
 * names are kept in the activity log.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { ApiError, api } from '@/lib/api';

export function RenameAllDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const preview = useQuery({
    queryKey: ['rename-all-preview'],
    queryFn: () => api.renameAllFiles(false),
    enabled: open,
    staleTime: 0,
    gcTime: 0,
  });

  const apply = useMutation({
    mutationFn: () => api.renameAllFiles(true),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  useEffect(() => {
    if (open) apply.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const count = preview.data?.count ?? 0;
  const error = preview.error ?? apply.error;

  return (
    <Modal
      open={open}
      onClose={() => (apply.isPending ? undefined : onClose())}
      title={t('renameAll.title')}
      description={t('renameAll.hint')}
      footer={
        apply.isSuccess ? (
          <button type="button" className="btn-primary" onClick={onClose}>
            {t('common.close')}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={!preview.data || count === 0 || apply.isPending}
              onClick={() => apply.mutate()}
            >
              {apply.isPending ? t('common.saving') : t('renameAll.apply', { count })}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={apply.isPending}>
              {t('common.cancel')}
            </button>
          </>
        )
      }
    >
      {preview.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : apply.isSuccess ? (
        <p className="text-sm">{t('renameAll.done', { count: apply.data.count })}</p>
      ) : preview.data ? (
        count === 0 ? (
          <p className="text-sm">{t('renameAll.nothing')}</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-semibold">{t('renameAll.count', { count })}</p>
            <ul className="card divide-y divide-border text-xs">
              {preview.data.examples.map((r) => (
                <li key={r.id} className="px-3 py-2 space-y-0.5">
                  <div className="text-muted line-through break-all">{r.from}</div>
                  <div className="font-medium break-all">{r.to}</div>
                </li>
              ))}
            </ul>
            {count > preview.data.examples.length ? (
              <p className="text-xs text-muted">
                {t('renameAll.more', { count: count - preview.data.examples.length })}
              </p>
            ) : null}
          </div>
        )
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-danger mt-3">
          {error instanceof ApiError ? error.message : t('common.saveError')}
        </p>
      ) : null}
    </Modal>
  );
}
